import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { useId, useLayoutEffect, useRef, useState } from 'react'

type TextDefaultProps = {
    children: string
    className?: string
    fontSize?: number
    maxCharsPerLine?: number
    textColor?: string
    outlineColor?: string
    dropShadowColor?: string
    fontFamily?: 'nunito' | 'grandstander'
    fontWeight?: string | number
    italic?: boolean
    leading?: number
    tracking?: string | number
}

const fontFamilyByVariant: Record<NonNullable<TextDefaultProps['fontFamily']>, string> = {
    nunito: 'Nunito, ui-sans-serif, system-ui, sans-serif',
    grandstander: 'Grandstander, ui-sans-serif, system-ui, sans-serif',
}

type TextBounds = {
    x: number
    width: number
}

function splitTextIntoLines(text: string, maxCharsPerLine: number) {
    const paragraphs = text.split(/\r?\n/)
    const lines: string[] = []

    for (const paragraph of paragraphs) {
        if (!paragraph.trim()) {
            lines.push('')
            continue
        }

        const words = paragraph.trim().split(/\s+/)
        let currentLine = ''

        for (const word of words) {
            const nextLine = currentLine ? `${currentLine} ${word}` : word

            if (nextLine.length > maxCharsPerLine && currentLine) {
                lines.push(currentLine)
                currentLine = word
            } else {
                currentLine = nextLine
            }
        }

        if (currentLine) {
            lines.push(currentLine)
        }
    }

    return lines
}

export default function TextDefault({
    children,
    className,
    fontSize = 16,
    maxCharsPerLine = 48,
    textColor = '#C8E3F7',
    outlineColor = '#5486AC',
    dropShadowColor = '#5486AC',
    fontFamily = 'nunito',
    fontWeight = 700,
    italic = false,
    leading = 1.5,
    tracking = '-0.04em',
}: TextDefaultProps) {
    const baseId = useId().replace(/:/g, '')
    const gradientId = `${baseId}-text-default-gradient`
    const shadowId = `${baseId}-text-default-shadow`
    const textRef = useRef<SVGTextElement>(null)
    const [textBounds, setTextBounds] = useState<TextBounds | null>(null)
    const lines = splitTextIntoLines(children, maxCharsPerLine)
    const lineHeight = fontSize * leading
    const strokeWidth = Math.max(1, fontSize * 0.125)
    const viewBoxHeight = lines.length * lineHeight + strokeWidth * 4
    const boundsPadding = strokeWidth * 4
    const longestLineLength = Math.max(...lines.map((line) => line.length), 1)
    const estimatedWidth = longestLineLength * fontSize * 0.6 + boundsPadding * 2
    const viewBoxX = textBounds?.x ?? -estimatedWidth / 2
    const viewBoxWidth = textBounds?.width ?? estimatedWidth
    const lineKey = lines.join('\n')

    const isStele = useSteleBreakpoint()

    useLayoutEffect(() => {
        let isCancelled = false

        const measureText = () => {
            const textElement = textRef.current

            if (!textElement) return

            const box = textElement.getBBox()
            const nextBounds = {
                x: box.x - boundsPadding,
                width: box.width + boundsPadding * 2,
            }

            if (isCancelled) return

            setTextBounds((currentBounds) => {
                if (
                    currentBounds
                    && Math.abs(currentBounds.x - nextBounds.x) < 0.5
                    && Math.abs(currentBounds.width - nextBounds.width) < 0.5
                ) {
                    return currentBounds
                }

                return nextBounds
            })
        }

        const frameId = window.requestAnimationFrame(measureText)

        void document.fonts?.ready.then(() => {
            if (!isCancelled) measureText()
        })

        return () => {
            isCancelled = true

            window.cancelAnimationFrame(frameId)
        }
    }, [boundsPadding, fontFamily, fontSize, fontWeight, italic, lineKey, tracking])

    return (
        <svg
            role='img'
            aria-label={children}
            viewBox={`${viewBoxX} 0 ${viewBoxWidth} ${viewBoxHeight}`}
            className={className}
            style={{
                display: 'block',
                width: `${viewBoxWidth}px`,
                height: `${viewBoxHeight}px`,
                overflow: 'visible',
            }}
        >
            <defs>
                <linearGradient
                    id={gradientId}
                    x1='0'
                    y1={viewBoxHeight}
                    x2='0'
                    y2='0'
                    gradientUnits='userSpaceOnUse'
                >
                    <stop offset='0%' stopColor={textColor} />
                    <stop offset='100%' stopColor='#FFFFFF' />
                </linearGradient>
                <filter
                    id={shadowId}
                    x='-10%'
                    y='-10%'
                    width='120%'
                    height='140%'
                    colorInterpolationFilters='sRGB'
                >
                    <feDropShadow
                        dx='0'
                        dy={isStele ? '4' : '1'}
                        stdDeviation='0'
                        floodColor={dropShadowColor}
                        floodOpacity='1'
                    />
                </filter>
            </defs>

            <text
                ref={textRef}
                x='0'
                y={strokeWidth * 2 + fontSize}
                textAnchor='middle'
                dominantBaseline='alphabetic'
                fontFamily={fontFamilyByVariant[fontFamily]}
                fontSize={fontSize}
                fontStyle={italic ? 'italic' : undefined}
                fontWeight={fontWeight}
                letterSpacing={tracking}
                fill={`url(#${gradientId})`}
                stroke={outlineColor}
                strokeWidth={strokeWidth}
                strokeLinejoin='round'
                paintOrder='stroke fill'
                filter={`url(#${shadowId})`}
            >
                {lines.map((line, index) => (
                    <tspan
                        key={`${line}-${index}`}
                        x='0'
                        dy={index === 0 ? 0 : lineHeight}
                    >
                        {line}
                    </tspan>
                ))}
            </text>
        </svg>
    )
}
